import { __rest } from 'tslib';
import React, { useState, useRef, useEffect, useContext } from 'react';
import { Partner } from './types.es.js';
import { extractFromPath } from '@teko/utils';
import { ModalConfirm } from '@teko/ui';
import { logger } from '@teko/log';

const IBANKING_LOGIN_DATA = 'ibanking_login_data';
const FEDERATION_PARTNER = 'federation';
const CONSUMERAPP_PARTNER = 'consumerapp';
const LOCALSTORAGE_REFRESH_TOKEN = 'REFRESH_TOKEN';
const LOCALSTORAGE_BACK_APP_URL = 'BACK_APP_URL';
const mergeInfo = (login, profile) => {
    return Object.assign(Object.assign(Object.assign({}, login), profile), { userId: login.userId || profile.userId, name: profile.name || login.name, telephone: profile.telephone || login.phone, avatar: login.picture || profile.avatar });
};
const AuthContext = React.createContext(null);
const AuthContextProvider = ({ children, userService, authService, partner, scopes, router, }) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [show, setShow] = useState(false);
    const lastRenewTokenTime = useRef(0);
    const updateProfile = async (profileInput) => {
        const profile = await userService.updateProfile(profileInput);
        const updatedProfile = Object.assign(Object.assign({}, user), profile);
        setUser(updatedProfile);
        return updatedProfile;
    };
    const fetchProfile = async (params = {}, infoToMerged) => {
        try {
            setLoading(true);
            const profile = await userService.myProfile(params);
            setUser(mergeInfo(infoToMerged || authService.getLoginInfo(), profile));
        }
        catch (e) {
            setUser(null);
            logger.error(e);
        }
        finally {
            setLoading(false);
        }
    };
    const _loginWithConsumerApp = async (userInfo) => {
        const loginInfo = await authService.loginWithTerra(userInfo);
        await fetchProfile({}, loginInfo);
    };
    const _loginWithIBanking = async (queries) => {
        const isSacombankMb = partner === 'sacombankmb';
        if (isSacombankMb) {
            queries.signature = queries.signature || queries.data;
            queries.partner_code = queries.partner_code || '12901';
        }
        const body = await authService.loginWithIBanking(queries);
        const { ibanking_info: { partner_code, token: partner_token }, } = body;
        await fetchProfile(partner_code && partner_token
            ? {
                partnerCode: partner_code,
                partnerToken: partner_token,
            }
            : {});
    };
    const _loginWithFederation = async (queries) => {
        const terraIdToken = queries.terraIdToken;
        const authorizer = queries.authorizer;
        if (!terraIdToken || !authorizer) {
            throw new Error('Missing federarion login config');
        }
        const { refresh_token } = await authService.loginWithFederation(terraIdToken, authorizer);
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN, refresh_token);
        await fetchProfile({});
    };
    const _refreshToken = async () => {
        const refreshToken = localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem(LOCALSTORAGE_REFRESH_TOKEN);
        if (!refreshToken) {
            return;
        }
        const { refresh_token } = await authService.refreshToken(refreshToken);
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN, refresh_token);
        await fetchProfile({});
    };
    const _getPartnerType = (partnerType) => {
        switch (partnerType) {
            case CONSUMERAPP_PARTNER:
                return Partner.CONSUMERAPP;
            case FEDERATION_PARTNER:
                return Partner.FEDERATION;
            default:
                return Partner.IBANKING;
        }
    };
    const loginWithPartner = async (queries, autoRenew = true, autoRedirect = true) => {
        const { redirect_uri, backAppUrl } = queries, storeData = __rest(queries, ["redirect_uri", "backAppUrl"]);
        sessionStorage.setItem(IBANKING_LOGIN_DATA, JSON.stringify(storeData));
        if (backAppUrl) {
            localStorage.setItem(LOCALSTORAGE_BACK_APP_URL, backAppUrl);
        }
        const _loginWithPartner = async () => {
            try {
                const partnerType = _getPartnerType(queries.partnerType);
                switch (partnerType) {
                    case Partner.FEDERATION: {
                        await _loginWithFederation(queries);
                        break;
                    }
                    case Partner.IBANKING: {
                        await _loginWithIBanking(queries);
                        break;
                    }
                    case Partner.CONSUMERAPP: {
                        await _loginWithConsumerApp(queries.userInfo);
                        break;
                    }
                }
                const isSacombankMb = partner === 'sacombankmb';
                if (router) {
                    if (queries === null || queries === void 0 ? void 0 : queries.redirect_uri) {
                        const extractLink = extractFromPath(queries.redirect_uri);
                        typeof extractLink === 'object'
                            ? router.push(extractLink, queries.redirect_uri)
                            : router.push(extractLink);
                        return;
                    }
                    if (autoRedirect) {
                        if (isSacombankMb)
                            return setShow(true);
                        router.push('/');
                    }
                }
                else {
                    console.warn('auth-react: router is not set');
                }
            }
            catch (e) {
                logger.error(e);
                if (window.location.pathname.includes('/login'))
                    alert('Đăng nhập thất bại!');
            }
        };
        _loginWithPartner();
        if (autoRenew) {
            setInterval(async () => {
                const now = new Date().getTime();
                const diff = now - lastRenewTokenTime.current;
                if (diff > 3000000) {
                    lastRenewTokenTime.current = now;
                    const partnerType = _getPartnerType(queries.partnerType);
                    switch (partnerType) {
                        case Partner.FEDERATION: {
                            await _refreshToken();
                            break;
                        }
                        case Partner.IBANKING: {
                            _loginWithPartner();
                            break;
                        }
                    }
                }
            }, 3000);
        }
    };
    const initIam = async () => {
        if (partner)
            return;
        await authService.initialize(scopes);
        const loggedInUser = authService.getLoginInfo();
        if (loggedInUser === null || loggedInUser === void 0 ? void 0 : loggedInUser.userId) {
            await fetchProfile();
            const previousLocation = authService.getAndClearBeforeLoginUrl();
            if (previousLocation && router) {
                router.push(previousLocation);
            }
            else if (!router) {
                console.warn('auth-react: router is not set');
            }
        }
        else {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (partner) {
            try {
                const queries = sessionStorage.getItem(IBANKING_LOGIN_DATA);
                if (queries) {
                    const queriesObj = JSON.parse(queries);
                    if (!window.location.pathname.includes('/login') &&
                        Object.keys(queriesObj).length) {
                        if (queriesObj.partnerType === FEDERATION_PARTNER)
                            _refreshToken();
                        else
                            loginWithPartner(queriesObj, true, false);
                    }
                }
            }
            catch (e) {
                logger.error('Relogin ibanking false', e);
            }
        }
        else {
            initIam();
        }
    }, []);
    const { challenge } = router.query;
    useEffect(() => {
        if (challenge && !router.route.includes('/login')) {
            authService.requestLogout(challenge);
        }
    }, [challenge]);
    return (React.createElement(AuthContext.Provider, { value: {
            user,
            loading,
            loginWithPartner,
            authService,
            userService,
            updateProfile,
            initIam,
            setUser,
        } },
        React.createElement(ModalConfirm, { show: show, okText: "\u0110\u1ED3ng \u00FD", ok: () => {
                router === null || router === void 0 ? void 0 : router.push('/');
                setShow(false);
            }, cancelText: "Quay l\u1EA1i", cancel: () => {
                window.location.href = '/?action=backtoapp';
            }, message: "D\u1ECBch v\u1EE5 Mua s\u1EAFm VNSHOP \u0111\u01B0\u1EE3c cung c\u1EA5p b\u1EDFi VNPAY. Chi ti\u1EBFt li\u00EAn h\u1EC7 hotline VNPAY: 1900555577 \u0111\u1EC3 \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3." }),
        children));
};
function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be inside a AuthContextProvider with a state value');
    }
    return context;
}

export { AuthContextProvider, useAuth };
